import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.png';
import { authAPI } from '../services/api';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canRegisterSuperAdmin, setCanRegisterSuperAdmin] = useState(true);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const {
    register: registerForm,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Check if super admin registration is allowed
  useEffect(() => {
    const checkSuperAdminRegistration = async () => {
      try {
        const response = await authAPI.canRegisterSuperAdmin();
        setCanRegisterSuperAdmin(response.data.canRegister);
      } catch (error) {
        console.error('Error checking super admin registration:', error);
        // Default to false if there's an error
        setCanRegisterSuperAdmin(false);
      }
    };

    checkSuperAdminRegistration();
  }, []);

  // If super admin can't be registered and user is in registration mode, switch to login
  useEffect(() => {
    if (!canRegisterSuperAdmin && isRegistering) {
      setIsRegistering(false);
      reset();
    }
  }, [canRegisterSuperAdmin, isRegistering, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isRegistering) {
        const result = await register(data);
        if (result.success) {
          setIsRegistering(false);
          reset();
          // Recheck if super admin can still be created
          const response = await authAPI.canRegisterSuperAdmin();
          setCanRegisterSuperAdmin(response.data.canRegister);
        }
      } else {
        const result = await login(data);
        if (result.success) {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    // Don't allow toggling to register mode if super admin exists
    if (!isRegistering && !canRegisterSuperAdmin) {
      toast.error('Super Admin already exists. Please contact your administrator.');
      return;
    }
    setIsRegistering(!isRegistering);
    reset();
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-4 sm:py-8 md:py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-200/20 to-transparent rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-200/20 to-transparent rounded-full blur-3xl"
        />
      </div>

      {/* Top Left Logo - Hidden on mobile, visible on md+ */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
        className="hidden md:block absolute top-4 md:top-8 left-4 md:left-8 z-10"
      >
        <img 
          src={logo} 
          alt="Prime Source Logo" 
          className="h-20 w-20 md:h-32 md:w-32 object-contain drop-shadow-lg hover:scale-105 transition-transform duration-300"
        />
      </motion.div>

      {/* Bottom Right Company Info - Repositioned for mobile */}
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 100 }}
        className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 md:bottom-8 md:right-8 z-10"
      >
        <a
          href="https://primesourcellp.com/Home"
          target="_blank"
          rel="noopener noreferrer"
          className="group block px-2 py-1 sm:px-4 sm:py-2 md:px-5 md:py-3 transition-all duration-300 hover:scale-105"
        >
          <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1">Powered by</p>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <span className="text-xs sm:text-base md:text-lg font-bold text-primary-700 group-hover:text-primary-800 transition-colors">
              Primesourcellp
            </span>
            <svg 
              className="w-3 h-3 sm:w-4 sm:h-4 text-primary-600 transform group-hover:translate-x-1 transition-transform" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </div>
        </a>
      </motion.div>

      {/* Main Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="relative max-w-xl w-full z-20 mt-8 sm:mt-0"
      >
        {/* Mobile Logo - Centered at top */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="md:hidden flex justify-center mb-6"
        >
          <img 
            src={logo} 
            alt="Prime Source Logo" 
            className="h-20 w-20 object-contain drop-shadow-lg"
          />
        </motion.div>

        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          {/* Welcome Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-primary-100 text-primary-700 text-xs sm:text-sm font-semibold mb-4 sm:mb-6"
          >
            <span className="relative flex h-2 w-2 mr-1.5 sm:mr-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
            </span>
            Welcome to TrackPrime
          </motion.div>

          {/* Page Title */}
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2"
          >
            {isRegistering ? 'Create Super Admin Account' : 'Sign In'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-xs sm:text-sm text-gray-600 px-2"
          >
            {isRegistering 
              ? 'Set up the first admin account for your system'
              : 'Enter your credentials to access your account'
            }
          </motion.p>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="bg-white/90 backdrop-blur-xl p-4 sm:p-6 md:p-10 rounded-xl sm:rounded-2xl shadow-2xl border border-white/20"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="space-y-4 sm:space-y-5">
            <div>
              <label htmlFor="username" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                {isRegistering ? 'Username' : 'Username or Email'}
              </label>
              <input
                {...registerForm('username', { required: isRegistering ? 'Username is required' : 'Username or Email is required' })}
                type="text"
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                placeholder={isRegistering ? "Enter your username" : "Enter your username or email"}
              />
              {errors.username && (
                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>

            {isRegistering && (
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Email
                </label>
                <input
                  {...registerForm('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            )}

            {isRegistering && (
              <>
                <div>
                  <label htmlFor="firstName" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    First Name
                  </label>
                  <input
                    {...registerForm('firstName', { required: 'First name is required' })}
                    type="text"
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && (
                    <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    Last Name
                  </label>
                  <input
                    {...registerForm('lastName', { required: 'Last name is required' })}
                    type="text"
                    className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && (
                    <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  {...registerForm('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 pr-10 sm:pr-12 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600">{errors.password.message}</p>
              )}
              {!isRegistering && (
                <div className="flex justify-end mt-1.5 sm:mt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-semibold transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="pt-1 sm:pt-2">
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 sm:py-4 px-4 border border-transparent text-sm sm:text-base font-semibold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <div className="spinner"></div>
              ) : (
                <>
                  {isRegistering ? (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      <span>Create Account</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      <span>Sign In</span>
                    </>
                  )}
                </>
              )}
            </motion.button>
          </div>

          {canRegisterSuperAdmin && (
            <div className="text-center mt-3 sm:mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="button"
                onClick={toggleMode}
                className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-semibold transition-colors relative group px-2"
              >
                <span className="block">
                  {isRegistering 
                    ? 'Already have an account? Sign in'
                    : 'Need to create a super admin account? Register'
                  }
                </span>
                <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-primary-600 group-hover:w-full transition-all duration-300"></span>
              </motion.button>
            </div>
          )}
        </motion.form>

        {/* Decorative Elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full"
        >
          <div className="absolute top-0 left-0 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-primary-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;

